import { DefaultFilters, DefaultFilterValue } from "./queryClient";

export interface VisibilityFlags {
    canViewIndividualData: boolean;
    canViewUserPii: boolean;
}

type VisibilityFlag = keyof VisibilityFlags;

type EndpointFieldPolicy = Partial<Record<VisibilityFlag, string[]>>;

const endpointFieldPolicies: Record<string, EndpointFieldPolicy> = {
    '/reports/students/': {
        canViewIndividualData: ['average_score'],
        canViewUserPii: ['full_name', 'email']
    },
    '/reports/student-activities/': {
        canViewIndividualData: ['best_score'],
        canViewUserPii: ['student.full_name', 'student.email']
    },
    '/reports/student-courses/': {
        canViewIndividualData: ['score', 'provisional_score', 'completion', 'completed', 'completed_datetime']
    },
    '/reports/student-exercise-performance/': {
        canViewIndividualData: ['first_score', 'best_score', 'improvement_delta'],
        canViewUserPii: ['student.full_name', 'student.email']
    },
    '/reports/student-exercise-attempts/': {
        canViewIndividualData: ['score', 'passed', 'feedback'],
        canViewUserPii: ['student.full_name', 'student.email', 'feedback']
    },
};

const getExcludeList = (
    policy: EndpointFieldPolicy,
    flags: VisibilityFlags,
    currentExclude?: DefaultFilterValue
) => {
    const exclude = new Set(
        typeof currentExclude === 'string'
            ? currentExclude.split(',').map(item => item.trim()).filter(Boolean)
            : []
    );

    (Object.keys(flags) as VisibilityFlag[]).forEach(flag => {
        if (!flags[flag]) {
            policy[flag]?.forEach(field => exclude.add(field));
        }
    });

    return exclude.size > 0 ? Array.from(exclude).join(',') : undefined;
};

const getEndpointPolicy = (endpoint: string): EndpointFieldPolicy => {
    if (!endpoint.endsWith('/')) endpoint += '/';
    return endpointFieldPolicies[Object.keys(endpointFieldPolicies).find(path =>
        endpoint === path || !!endpoint.match(new RegExp(`^${path}[^/]+/?$`))
    ) || ''] || {};
}

export const buildVisibilityFilters = (
    endpoint: string,
    flags: VisibilityFlags,
    baseFilters: DefaultFilters = {}
): DefaultFilters => {
    const exclude = getExcludeList(getEndpointPolicy(endpoint), flags, baseFilters.exclude);
    return exclude ? {...baseFilters, exclude} : baseFilters;
};
